import { Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FontAwesomeSharedModule } from '../../font-awesome/font-awesome';
import { LabFacade } from '../../../views/labs/facade/lab.facade';
import { UserMgmtFacade } from '../../../views/user-management/facade/user-mgmt.facade';
import { ILab } from '../../../views/labs/models/Labs';
import { selectableLabsFor } from '../../utils/selectable-labs';
import { labUiStatus } from '../../utils/lab-status';
import { getStatusDotColor } from '../../utils/project-utils';
import { ProjectStatus } from '../../models/project-status';
import { labSwitchTarget } from '../../utils/lab-scoped-nav';

// The active context chip in the header. Lab users switch between their lab
// memberships. Platform admins also get a special platform-wide context.
@Component({
  selector: 'app-lab-switcher',
  imports: [CommonModule, FontAwesomeSharedModule],
  templateUrl: './lab-switcher.component.html',
})
export class LabSwitcherComponent {
  private readonly labFacade = inject(LabFacade);
  private readonly userMgmtFacade = inject(UserMgmtFacade);
  private readonly router = inject(Router);

  isPlatformAdmin = computed(() => this.userMgmtFacade.getMeUserSignal()?.is_platform_admin === true);

  // The lab to advertise in the header, resolved against the memberships on
  // the current /me user. A selection left over from another account, or from
  // a lab this user was removed from, resolves to null and the chip stays
  // hidden rather than naming a lab they cannot open. Memberships rather than
  // labList: they arrive with /me and are loaded on every authorized page,
  // whereas labList is empty until GET /api/labs returns.
  activeLab = computed<ILab | null>(() => {
    const selected = this.labFacade.selectedLabSignal();
    if (!selected) return null;
    const membership = (this.userMgmtFacade.getMeUserSignal()?.lab_memberships ?? []).find(
      (lab) => lab.id === selected.id
    );
    if (!membership) return null;
    // Prefer the membership's name; it came with this request, while the
    // persisted lab may be a rename behind.
    return { ...selected, display_name: membership.display_name };
  });
  selectableLabs = computed<ILab[]>(() =>
    selectableLabsFor(this.labFacade.labListSignal(), this.userMgmtFacade.getMeUserSignal())
  );
  // Platform admins with none of their own labs selected sit in the
  // platform-wide context instead.
  isPlatformOverview = computed(() => this.isPlatformAdmin() && !this.activeLab());
  showSwitcher = computed(() => this.isPlatformAdmin() || !!this.activeLab());

  // Status of each lab in the dropdown, so switching away from a healthy lab
  // into an inactive one is a visible choice rather than a surprise.
  uiStatus(lab: ILab): ProjectStatus {
    return labUiStatus(lab);
  }

  statusDotColor(lab: ILab): string {
    return getStatusDotColor(labUiStatus(lab));
  }

  selectLab(lab: ILab): void {
    this.closeDropdown();
    this.labFacade.setSelectedLab(lab);
    // Stay on the current section (Sequences/Projects/Lab Users) for the newly
    // selected lab; otherwise fall back to the dashboard.
    this.router.navigate(labSwitchTarget(this.router.url, lab.id));
  }

  selectPlatformOverview(): void {
    this.closeDropdown();
    this.labFacade.clearSelectedLab();
    this.router.navigate(['/dashboard']);
  }

  goToLabSelection(): void {
    this.closeDropdown();
    if (this.isPlatformAdmin()) {
      this.labFacade.clearSelectedLab();
      this.router.navigate(['/labs-projects']);
      return;
    }
    this.router.navigate(['/select-lab']);
  }

  // daisyUI keeps the dropdown open while an item holds focus, so blur to close.
  private closeDropdown(): void {
    (document.activeElement as HTMLElement | null)?.blur();
  }
}
